import React from 'react';
import * as Yup from 'yup';
import { JFormikData, selectUser, sortString } from '@centerpiece/components';
import { FormikHelpers, getIn } from 'formik';
import { statesConfig } from '@centerpiece/components';
import {
  Box,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import {
  RestartAlt,
  MailOutlineOutlined,
  PersonOutlineOutlined,
  PhoneOutlined,
  NotesOutlined,
  CorporateFareOutlined,
  Key,
  Groups,
  InfoOutlined,
} from '@mui/icons-material';
import {
  useCreateOrganizationMutation,
  useCreateRoleMutation,
  useGetContactQuery,
  useGetProjectTypesQuery,
  useGetRolesQuery,
  useGetTeamsQuery,
} from 'store/api';
import { useSelector } from 'react-redux';
import { Contact, Organization } from 'store/api/models';

export const useContactForm = ({
  edit = false,
  minimal = false,
  externalSubmit = false,
  role = true,
  account = false,
  roleType = 'Internal',
  organizations,
  enableOrganization = false,
}: {
  edit?: boolean;
  minimal?: boolean;
  externalSubmit?: boolean;
  role?: boolean;
  account?: boolean;
  roleType?: 'Internal' | 'External';
  organizations?: Organization[];
  enableOrganization?: boolean;
}): JFormikData => {
  const user = useSelector(selectUser);
  const { data: contact = {} as Contact, isLoading } = useGetContactQuery(
    { id: user.contact },
    { skip: !user.contact }
  );
  const admin = contact?.form?.admin === 'Yes';
  const { data: roles = [] } = useGetRolesQuery({ params: { type: roleType } });
  const { data: teams = [] } = useGetTeamsQuery({});
  const { data: project_types = [] } = useGetProjectTypesQuery({});
  const [createRole] = useCreateRoleMutation();
  const [createOrganization] = useCreateOrganizationMutation();
  return {
    validationSchema: Yup.object({
      firstname: Yup.string().required('First name is required'),
      lastname: Yup.string().required('Last name is required'),
      email: Yup.string()
        .required('Email is required')
        .email('Invalid email address'),
      role:
        roleType === 'External'
          ? Yup.object().required('Please select an option').nullable()
          : Yup.object().nullable(),
      organization:
        roleType === 'External'
          ? Yup.object()
              .required('Please select an organization or create a new one')
              .nullable()
          : Yup.object().nullable(),
    }),
    form: [
      {
        precondition: (values: any) => !minimal,
        name: 'basic-fields',
        itemType: 'typography',
        jItemProps: {
          variant: 'overline',
          sx: { mb: '0 !important', pb: '0 !important' },
        },
        label: 'Basic Information',
      },
      {
        precondition: (values: any) => !minimal,
        name: 'basic-fields-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        precondition: (values: any) => !minimal,
        name: 'firstname',
        label: 'First Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <PersonOutlineOutlined color="secondary" />,
          },
        },
      },
      {
        precondition: (values: any) => !minimal,
        name: 'lastname',
        label: 'Last Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
      },
      {
        precondition: (values: any) => minimal,
        name: 'name',
        label: 'Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <PersonOutlineOutlined color="secondary" />,
          },
        },
      },
      {
        precondition: (values: any) => !minimal || values?.email,
        name: 'email',
        label: 'Email',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <MailOutlineOutlined color="secondary" />,
          },
        },
      },
      ...(account && admin
        ? [
            {
              name: 'form.job_title',
              label: 'Job Title',
              jItemProps: {
                InputProps: {
                  endAdornment: (
                    <Tooltip title="Outbound messaging will include your job title after your name">
                      <InfoOutlined color="secondary" />
                    </Tooltip>
                  ),
                },
              },
              itemType: 'textfield',
            },
            {
              name: 'form.authorization',
              label: 'Authorization Level',
              freeSolo: true,
              jItemProps: {
                freeSolo: true,
                renderOption: (props: any, option: any) => {
                  return (
                    <Box component="li" {...props}>
                      <span>
                        <b>{option.label}:&nbsp;</b>
                        <small>{option.description}</small>
                      </span>
                    </Box>
                  );
                },
                getOptionSelected: (option: any, value: any) =>
                  option.name === value.name, // Specify how to determine if an option is selected
                onChange: (
                  value: any,
                  name: string,
                  setFieldValue: Function
                ) => {
                  setFieldValue(name, value.name);
                },
                optionsFilter: createFilterOptions({
                  stringify: (option: any) => option.label,
                }),
              },
              itemType: 'autocomplete',
              options: [
                {
                  name: 'Individual',
                  label: 'Individual',
                  description:
                    'This user only has access to projects they create or get added to by their teammates.',
                },
                {
                  name: 'Organization Member',
                  label: 'Organization Member',
                  description:
                    'This user’s dashboard initially shows just their projects but they can use filters to get access to all of the organization’s projects.',
                },
                {
                  name: 'Team Member',
                  label: 'Team Member',
                  description:
                    'This user’s dashboard initially shows just their projects but they can use filters to get access to all of the team’s projects.',
                },
                {
                  name: 'Team Leader',
                  label: 'Team Leader',
                  description:
                    'This user has access to all of their team’s projects automatically.',
                },
                {
                  name: 'Executive',
                  label: 'Executive',
                  description:
                    'This user has access to all projects across the organization automatically.',
                },
              ],
            },
            // {
            //   name: 'form.authorization',
            //   label: 'Authorization Level',
            //   jItemProps: {
            //     InputProps: {
            //       // endAdornment: <Key color="secondary" />,
            //     },
            //   },
            //   itemType: 'autocomplete',
            //   options: ['Individual', 'Team Leader', 'Executive'],
            // },
            {
              name: 'form.team',
              label: 'Team',
              jItemProps: {
                InputProps: {
                  // endAdornment: <Groups color="secondary" />,
                },
              },
              itemType: 'autocomplete',
              options: teams,
            },
            {
              name: 'form.project_type',
              label: 'Default Project Type',
              itemType: 'autocomplete',
              options: project_types,
            },
            {
              name: 'form.admin',
              label: 'Admin',
              jItemProps: {
                InputProps: {
                  // endAdornment: <Key color="secondary" />,
                },
              },
              itemType: 'autocomplete',
              options: ['No', 'Yes'],
            },
          ]
        : account && !admin
        ? [
            {
              precondition: (values: any) => !!getIn(values, 'form.team'),
              name: 'form.team',
              label: 'Team',
              disabled: true,
              jItemProps: {
                disabled: true,
              },
              itemType: 'autocomplete',
              options: teams,
            },
            {
              name: 'form.project_type',
              label: 'Default Project Type',
              itemType: 'autocomplete',
              options: project_types,
            },
            {
              name: 'form.job_title',
              label: 'Job Title',
              jItemProps: {
                InputProps: {
                  endAdornment: (
                    <Tooltip title="Outbound messaging will include your job title after your name">
                      <InfoOutlined color="secondary" />
                    </Tooltip>
                  ),
                },
              },
              itemType: 'textfield',
            },
          ]
        : [
            {
              precondition: (values: any) => !!getIn(values, 'form.team'),
              name: 'form.team',
              label: 'Team',
              disabled: true,
              jItemProps: {
                disabled: true,
              },
              itemType: 'autocomplete',
              options: teams,
            },
          ]),
      {
        precondition: (values: any) => role && (!minimal || values?.role),
        name: 'role',
        label: roleType === 'External' ? 'Stakeholder Type' : 'Role',
        itemType: 'autocomplete',
        options: [...(roles || [])].sort((a, b) => sortString(a.name, b.name)),
        // freeSolo: true,
        jItemProps: {
          // freeSolo: true,
          addValue: async (
            name: any,
            newValue: any,
            values: any,
            formik: FormikHelpers<any>
          ) => {
            const res = await createRole({
              name: newValue?.inputValue,
              type: roleType,
            }).unwrap();
            formik.setFieldValue(name, res);
          },
          width: '25%',
          sx: { boxSizing: 'border-box', px: { xs: 0.5 }, minWidth: 130 },
        },
        gridProps: { xs: 12 },
      },
      ...(!account
        ? [
            {
              // precondition: (values: any) =>
              //   values?.organization && !values?.form?.organization,
              name: 'organization',
              label: 'Organization',
              // freeSoloFormat: (value: any) => value,
              disabled: enableOrganization ? false : true,
              jItemProps: {
                // freeSolo: true,
                addValue: async (
                  name: any,
                  newValue: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  const res = await createOrganization({
                    name: newValue?.inputValue,
                    role: values?.role?.id,
                  }).unwrap();
                  formik.setFieldValue(name, res);
                },
                InputProps: {
                  endAdornment: <CorporateFareOutlined color="secondary" />,
                },
              },
              itemType: 'autocomplete',
              options: organizations,
            },
          ]
        : []),
      // {
      //   precondition: (values: any) =>
      //     (roleType === 'External' && !values?.organization) ||
      //     (minimal && values?.form?.organization),
      //   name: 'form.organization',
      //   label: 'Organization',
      //   freeSoloFormat: (value: any) => value,
      //   freeSolo: true,
      //   jItemProps: {
      //     freeSolo: true,
      //     InputProps: {
      //       endAdornment: <CorporateFareOutlined color="secondary" />,
      //     },
      //   },
      //   itemType: 'autocomplete',
      //   options: organizations,
      // },
      ...(minimal
        ? []
        : [
            {
              precondition: (values: any) => !minimal,
              name: 'additional-fields',
              itemType: 'typography',
              jItemProps: {
                variant: 'overline',
                sx: { mb: '0 !important', pb: '0 !important' },
              },
              label: 'Additional Fields',
            },
            {
              precondition: (values: any) => !minimal,
              name: 'additional-fields-bottom',
              label: '',
              itemType: 'divider',
              gridProps: { sx: { paddingTop: '0px !important' } },
            },
            {
              precondition: (values: any) => {
                return (
                  !minimal ||
                  (values?.form?.custom_fields &&
                    values?.form?.custom_fields?.length > 0)
                );
              },
              name: 'form.custom_fields',
              itemType: 'fieldarray',
              label: 'Additional Field',
              gridProps: {
                // sx: { paddingTop: '10px !important' },
              },
              array: [
                {
                  precondition: (values: any) =>
                    !values?.form?.custom_fields?.[values.index]?.fieldtype,
                  name: 'fieldtype',
                  label: 'Type',
                  // disabled: edit,
                  jItemProps: edit ? { type: 'local-disabled' } : {},
                  itemType: 'autocomplete',
                  options: account
                    ? [
                        'Phone (Office)',
                        'Phone (Mobile)',
                        'Address',
                        'Notes',
                        'Custom',
                      ]
                    : [
                        'Job Title',
                        'Phone (Office)',
                        'Phone (Mobile)',
                        'Address',
                        'Notes',
                        'Custom',
                      ],
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Custom',
                  name: 'dynamic',
                  label: 'Custom Field',
                  ...(!edit
                    ? {
                        tooltip: {
                          title: (
                            <Typography variant="body1">
                              Please specify the title of the field
                            </Typography>
                          ),
                          open: true,
                          placement: 'top',
                          arrow: true,
                        },
                      }
                    : {}),
                  jItemProps: {
                    textFieldType: 'custom',
                    // labelFunc: (name: any, values: any, formik: FormikHelpers<any>) => {
                    //   return getIn(values, name) || 'Please enter the field label...'
                    // },
                    InputProps: {
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)?.value) {
                          return (
                            <InputAdornment position="end">
                              <Tooltip title={`Reset type`}>
                                <IconButton
                                  onClick={() => {
                                    formik.setFieldValue(name, undefined);
                                    formik.setFieldValue(
                                      name.replaceAll('dynamic', 'fieldtype'),
                                      undefined
                                    );
                                  }}
                                >
                                  <RestartAlt color="secondary" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                      'Job Title' && !values?.form?.job_title,
                  name: 'job_title',
                  label: 'Job Title',
                  jItemProps: {
                    InputProps: {
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('job_title', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Notes',
                  name: 'notes',
                  label: 'Notes',
                  jItemProps: {
                    multiline: true,
                    rows: 4,
                    variant: 'outlined',
                    InputProps: {
                      endAdornment: <NotesOutlined color="secondary" />,
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('notes', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Phone (Mobile)',
                  name: 'phone_mobile',
                  label: 'Phone (Mobile)',
                  // disabled: edit,
                  jItemProps: {
                    type: 'phone',
                    InputProps: {
                      endAdornment: <PhoneOutlined color="secondary" />,
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('phone', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Phone (Office)',
                  name: 'phone_office',
                  label: 'Phone (Office)',
                  // disabled: edit,
                  jItemProps: {
                    type: 'phone',
                    InputProps: {
                      endAdornment: <PhoneOutlined color="secondary" />,
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('phone', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Address',
                  name: '',
                  label: '',
                  itemType: 'paper',
                  jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
                  array: [
                    {
                      name: 'address-label',
                      label: 'Address',
                      itemType: 'typography',
                      jItemProps: { variant: 'overline' },
                    },
                    {
                      name: 'address-label-bottom',
                      label: '',
                      itemType: 'divider',
                      gridProps: { sx: { paddingTop: '0px !important' } },
                    },
                    {
                      name: 'form.address',
                      itemType: 'textfield',
                      label: 'Address',
                      gridProps: { xs: 12 },
                    },
                    {
                      name: 'form.city',
                      itemType: 'textfield',
                      label: 'City',
                      gridProps: { xs: 12, sm: 4 },
                    },
                    {
                      name: 'form.state',
                      label: 'State',
                      itemType: 'autocomplete',
                      options: Object.keys(statesConfig),
                      gridProps: { xs: 12, sm: 4 },
                    },
                    {
                      name: 'form.zipcode',
                      itemType: 'textfield',
                      label: 'Postal Code',
                      gridProps: { xs: 12, sm: 4 },
                    },
                  ],
                },
              ],
            },
            {
              precondition: (values: any) => !externalSubmit,
              name: 'submit',
              itemType: 'button',
              jItemProps: { type: 'submit' },
              label: `${edit ? 'SAVE' : 'CREATE'} ${
                account ? 'ACCOUNT' : 'CONTACT'
              }`,
              gridProps: { xs: 12, sm: 6, md: 5, mx: 'auto' },
            },
          ]),
    ],
  } as JFormikData;
};

export const useContactTooltip = (): JFormikData => {
  return {
    validationSchema: Yup.object(),
    form: [
      {
        precondition: (values: any) => values?.firstname && !values?.name,
        name: 'firstname',
        label: 'First Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <PersonOutlineOutlined color="secondary" />,
          },
        },
      },
      {
        precondition: (values: any) => values?.lastname && !values?.name,
        name: 'lastname',
        label: 'Last Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
      },
      {
        precondition: (values: any) => values?.name,
        name: 'name',
        label: 'Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <PersonOutlineOutlined color="secondary" />,
          },
        },
      },
      {
        precondition: (values: any) => values?.email,
        name: 'email',
        label: 'Email',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <MailOutlineOutlined color="secondary" />,
          },
        },
      },
      {
        precondition: (values: any) => values?.form?.job_title,
        name: 'form.job_title',
        label: 'Job Title',
        jItemProps: {
          InputProps: {
            endAdornment: (
              <Tooltip title="Outbound messaging will include your job title after your name">
                <InfoOutlined color="secondary" />
              </Tooltip>
            ),
          },
        },
        itemType: 'textfield',
      },

      {
        precondition: (values: any) => values?.role,
        name: 'role',
        label: 'Stakeholder Type',
        itemType: 'autocomplete',
        jItemProps: {
          width: '25%',
          sx: { boxSizing: 'border-box', px: { xs: 0.5 }, minWidth: 130 },
        },
        gridProps: { xs: 12 },
      },
      {
        precondition: (values: any) =>
          values?.organization && !values?.form?.organization,
        name: 'organization',
        label: 'Organization',
        // freeSoloFormat: (value: any) => value,
        disabled: true,
        jItemProps: {
          InputProps: {
            endAdornment: <CorporateFareOutlined color="secondary" />,
          },
        },
        itemType: 'autocomplete',
      },
      {
        precondition: (values: any) => {
          return (
            values?.form?.custom_fields &&
            values?.form?.custom_fields?.length > 0
          );
        },
        name: 'form.custom_fields',
        itemType: 'fieldarray',
        label: 'Additional Field',
        gridProps: {
          // sx: { paddingTop: '10px !important' },
        },
        array: [
          {
            precondition: (values: any) =>
              !values?.form?.custom_fields?.[values.index]?.fieldtype,
            name: 'fieldtype',
            label: 'Type',
            // disabled: edit,
            itemType: 'autocomplete',
            options: [
              'Job Title',
              'Phone (Office)',
              'Phone (Mobile)',
              'Address',
              'Notes',
              'Custom',
            ],
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Custom',
            name: 'dynamic',
            label: 'Custom Field',
            jItemProps: {
              textFieldType: 'custom',
              // labelFunc: (name: any, values: any, formik: FormikHelpers<any>) => {
              //   return getIn(values, name) || 'Please enter the field label...'
              // },
              InputProps: {
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)?.value) {
                    return (
                      <InputAdornment position="end">
                        <Tooltip title={`Reset type`}>
                          <IconButton
                            onClick={() => {
                              formik.setFieldValue(name, undefined);
                              formik.setFieldValue(
                                name.replaceAll('dynamic', 'fieldtype'),
                                undefined
                              );
                            }}
                          >
                            <RestartAlt color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
                'Job Title' && !values?.form?.job_title,
            name: 'job_title',
            label: 'Job Title',
            jItemProps: {
              InputProps: {
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('job_title', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Notes',
            name: 'notes',
            label: 'Notes',
            jItemProps: {
              multiline: true,
              rows: 4,
              variant: 'outlined',
              InputProps: {
                endAdornment: <NotesOutlined color="secondary" />,
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('notes', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Phone (Mobile)',
            name: 'phone_mobile',
            label: 'Phone (Mobile)',
            // disabled: edit,
            jItemProps: {
              type: 'phone',
              InputProps: {
                endAdornment: <PhoneOutlined color="secondary" />,
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('phone', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Phone (Office)',
            name: 'phone_office',
            label: 'Phone (Office)',
            // disabled: edit,
            jItemProps: {
              type: 'phone',
              InputProps: {
                endAdornment: <PhoneOutlined color="secondary" />,
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('phone', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Address',
            name: '',
            label: '',
            itemType: 'paper',
            jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
            array: [
              {
                name: 'address-label',
                label: 'Address',
                itemType: 'typography',
                jItemProps: { variant: 'overline' },
              },
              {
                name: 'address-label-bottom',
                label: '',
                itemType: 'divider',
                gridProps: { sx: { paddingTop: '0px !important' } },
              },
              {
                name: 'form.address',
                itemType: 'textfield',
                label: 'Address',
                gridProps: { xs: 12 },
              },
              {
                name: 'form.city',
                itemType: 'textfield',
                label: 'City',
                gridProps: { xs: 12, sm: 4 },
              },
              {
                name: 'form.state',
                label: 'State',
                itemType: 'autocomplete',
                options: Object.keys(statesConfig),
                gridProps: { xs: 12, sm: 4 },
              },
              {
                name: 'form.zipcode',
                itemType: 'textfield',
                label: 'Postal Code',
                gridProps: { xs: 12, sm: 4 },
              },
            ],
          },
        ],
      },
    ],
  } as JFormikData;
};
